import { DEFAULT_LOGO_URL } from '@seek/libs-shared';
import { useTranslations } from '@vocab/react';
import {
  Actions,
  Alert,
  Box,
  Button,
  Heading,
  Hidden,
  IconArrow,
  Inline,
  Spread,
  Stack,
  Step,
  Stepper,
  Text,
} from 'braid-design-system';
import { Progress } from '../../type';
import translations from './.vocab';
import * as styles from './ReviewPageLayout.css';

type ReviewPageLayoutProps = React.PropsWithChildren<{
  alertText?: string;
  companyName?: string;
  hideStepper?: boolean;
  isContinueLoading?: boolean;
  logo?: string;
  onAlertClosed?: () => void;
  onBack?: () => void;
  onContinue?: () => void;
  progress?: Progress;
  subtitle?: string;
  testid?: string;
}>;

interface DesktopButtonProps {
  isContinueLoading: boolean;
  isLastStep: boolean;
  onBack?: () => void;
  onContinue?: () => void;
}

const DesktopButton = ({
  onBack,
  onContinue,
  isLastStep,
  isContinueLoading,
}: DesktopButtonProps) => {
  const { t } = useTranslations(translations);
  return (
    <Hidden below="tablet">
      <Spread space="large">
        <Actions>
          {onBack ? (
            <Button
              variant="soft"
              tone="neutral"
              icon={<IconArrow direction="left" />}
              onClick={onBack}
              data={{ testid: 'back-button-desktop' }}
            >
              {t('Back')}
            </Button>
          ) : null}
        </Actions>
        <Actions>
          {onContinue ? (
            <Button
              variant="solid"
              tone={!isLastStep ? 'formAccent' : 'brandAccent'}
              icon={!isLastStep ? <IconArrow direction="right" /> : undefined}
              iconPosition={!isLastStep ? 'trailing' : undefined}
              onClick={onContinue}
              data={{ testid: 'continue-button-desktop' }}
              loading={isContinueLoading}
            >
              {!isLastStep ? t('Continue') : t('Post review')}
            </Button>
          ) : null}
        </Actions>
      </Spread>
    </Hidden>
  );
};

export const ReviewPageLayout = ({
  progress = 1,
  companyName,
  subtitle,
  logo,
  children,
  testid,
  alertText,
  onContinue,
  onBack,
  onAlertClosed,
  isContinueLoading = false,
  hideStepper = false,
}: ReviewPageLayoutProps) => {
  const { t } = useTranslations(translations);
  const isLastStep =
    progress === Progress.YourFeedback || progress === Progress.SimplifiedPage;

  return (
    <Box
      data={testid ? { testid } : {}}
      paddingTop={{
        tablet: 'xxlarge',
        mobile: 'large',
      }}
    >
      <Stack space="xlarge">
        {logo && logo !== DEFAULT_LOGO_URL ? (
          <Box>
            <Box
              component="img"
              src={logo}
              alt={t('company logo')}
              className={styles.logoBox}
            />
          </Box>
        ) : null}
        <Stack space="large">
          <Heading level="1">
            {companyName
              ? t('Review working at {companyName}', { companyName })
              : t('Write a company review')}
          </Heading>
          {!hideStepper && (
            <Stepper label="Review Stepper" progress={progress} align="left">
              <Step>{t('Overall rating')}</Step>
              <Step>{t('Your role')}</Step>
              <Step>{t('Your experience')}</Step>
              <Step>{t('Your salary')}</Step>
              <Step>{t('Your feedback')}</Step>
            </Stepper>
          )}
        </Stack>
        {onAlertClosed ? (
          <Alert
            tone="critical"
            onClose={onAlertClosed}
            closeLabel="Close critical alert"
          >
            <Text>{alertText}</Text>
          </Alert>
        ) : null}
        <Box className={styles.questionsBox}>
          <Stack space="xlarge">
            {subtitle ? <Text size="standard">{subtitle}</Text> : null}
            <Box>{children}</Box>
            {progress === Progress.SimplifiedPage && (
              <DesktopButton
                isLastStep={isLastStep}
                isContinueLoading={isContinueLoading}
                onBack={onBack}
                onContinue={onContinue}
              />
            )}
          </Stack>
        </Box>
        {progress !== Progress.SimplifiedPage && (
          <DesktopButton
            isLastStep={isLastStep}
            isContinueLoading={isContinueLoading}
            onBack={onBack}
            onContinue={onContinue}
          />
        )}
        <Hidden above="mobile">
          <Inline space="medium" collapseBelow="tablet">
            {onContinue ? (
              <Button
                variant="solid"
                tone={!isLastStep ? 'formAccent' : 'brandAccent'}
                icon={!isLastStep ? <IconArrow direction="right" /> : undefined}
                iconPosition={!isLastStep ? 'trailing' : undefined}
                onClick={onContinue}
                data={{ testid: 'continue-button-mobile' }}
                loading={isContinueLoading}
              >
                {!isLastStep ? t('Continue') : t('Post review')}
              </Button>
            ) : null}
            {onBack ? (
              <Button
                variant="soft"
                tone="neutral"
                icon={<IconArrow direction="left" />}
                onClick={onBack}
                data={{ testid: 'back-button-mobile' }}
              >
                {t('Back')}
              </Button>
            ) : null}
          </Inline>
        </Hidden>
      </Stack>
    </Box>
  );
};
